import { until } from '@vueuse/core'
import type { FetchContext } from 'ofetch'
import type { FilterOptions, Order } from '~/types'
import {
  isOrderHistoryRequestForCustomer,
  mergeOrderHistoryList,
  type OrderHistoryResponse,
} from '~/utils/order-history'

interface FrequentOrderItem {
  name: string
  handle: string
  supplierHandle: string
  lastOrderedQuantity: number
  totalOrderQuantity: number
  lastOrderedReference?: string
  loading?: boolean
}

interface LastOrderedProductsResponse {
  products: FrequentOrderItem[]
  orderCount: number
  lastOrderDate: string | null
}

export type OrderRequestType = 'invoice' | 'orderStatus'

const MS_PER_DAY = 24 * 60 * 60 * 1000
const RECENT_ORDER_WINDOW_DAYS = 90

export const useOrderHistoryStore = defineStore('order-history', () => {
  const { baseURL } = useRuntimeConfig().app
  const { $i18n, $storeErrorLog } = useNuxtApp()
  const userStore = useUserStore()
  const accessToken = computed(() => userStore.accessToken)
  const customerHandle = computed(() => userStore?.customerHandle ?? undefined)

  // Abort when either credential is missing so we never hit
  // `/customers/undefined/order-history` (token-only abort left that race open).
  // Set Authorization here (not via reactive `headers`) so a login that assigns
  // token + handle in one tick does not also trip useFetch's `_fetchOptions`
  // watcher and cancel/restart the request that the URL/key change already started.
  const requestUrl = (request: unknown) =>
    typeof request === 'string' ? request : String((request as { url?: string })?.url ?? request ?? '')

  // Full order-history is 6–8s (nested items + scale prices). Welcome still
  // uses last-ordered-products (~300ms). The list is fetched once auth is
  // ready so the secondary menu can key off `totalOrders`.
  const isListActive = ref(false)

  const abortIfNotReady = {
    onRequest(ctx: FetchContext) {
      const token = unref(accessToken)
      const handle = unref(customerHandle)
      if (!token || !handle) {
        ctx.options.signal = AbortSignal.abort()
        return
      }
      if (!isListActive.value && requestUrl(ctx.request).includes('order-history')) {
        ctx.options.signal = AbortSignal.abort()
        return
      }
      ctx.options.headers.set('Authorization', `Bearer ${token}`)
    },
  }

  const itemsPerPage = computed(() => 10)
  const page = ref(1)
  const list = ref<Order[]>([])
  const totalOrders = ref(0)
  // Sticky "user has at least one order" flag for GTM / Product Fruits.
  // The secondary menu uses `totalOrders` so it only appears once the real
  // order-history payload has landed (avoids showing Last orders from
  // last-ordered-products while the list itself is still empty/stale).
  // `hasAnyOrders` only flips false→true on success and is not cleared by
  // `$reset` (submitOrder re-sets it immediately after). Cleared on session
  // end or customer-handle change — see auth watch below.
  const hasAnyOrders = ref(false)
  // First successful order-history response; used so GTM does not emit
  // customer_type "new" before we know whether the user has prior orders.
  const hasResolvedOrderHistory = ref(false)
  const filterOptions = ref<FilterOptions>({})
  const filter = ref<Record<string, string[]>>({})
  const query = computed(() => ({
    supplier: filter.value.suppliers ?? [],
    page: page.value,
  }))

  // Client plugins (gtm, product-fruits) instantiate this store at boot, often
  // before auth is ready. Token is applied in `onRequest` so login does not
  // double-fetch when token and handle are assigned together.
  const authWatchSources = [accessToken, customerHandle] as const

  // Shared list wipe used by `$reset` and the auth watch. Does not touch sticky
  // flags — those are session/customer scoped (see auth watch below).
  const clearListState = () => {
    list.value = []
    filter.value = {}
    page.value = 1
    totalOrders.value = 0
    filterOptions.value = {}
  }

  const applyOrderHistoryPayload = (payload: OrderHistoryResponse | null | undefined) => {
    if (!payload) return
    list.value = mergeOrderHistoryList(list.value, payload.orders)
    if (!totalOrders.value)
      totalOrders.value = payload.totalOrders
    if (payload.totalOrders > 0)
      hasAnyOrders.value = true
    hasResolvedOrderHistory.value = true
    if (!Object.keys(filterOptions.value).length && payload.filterOptions)
      filterOptions.value = payload.filterOptions
  }

  const {
    data: ordersData,
    pending: isLoading,
    refresh: reloadOrders,
  } = useFetch<OrderHistoryResponse>(
    () => `/api/intern/customers/${unref(customerHandle)}/order-history`,
    {
      baseURL,
      query,
      // Do not set a custom `key` that omits `query`: Nuxt's default key hashes
      // URL + query, so filter/page changes get a fresh entry. A handle-only key
      // reuses the unfiltered cache on watch (cause: 'watch') and skips $fetch,
      // leaving the list empty after toggleFilterValue clears it.
      ...abortIfNotReady,
      onResponse: ({ response, request }) => {
        if (!response.ok) return
        // Ignore a late response from a previous customer after an account switch.
        if (!isOrderHistoryRequestForCustomer(request, unref(customerHandle))) return
        applyOrderHistoryPayload(response._data)
      },
      onResponseError({ response }) {
        if (response.status === 401) void userStore.reloadAuth()
      },
    },
  )

  // `onResponse` does not run on Nuxt cache hits (`execute({ cause: 'initial' })`).
  // After a user switch the auth watch clears `hasAnyOrders`, so hydrate from
  // `ordersData` as well — that ref already tracks the current customer key.
  watch(ordersData, (data) => {
    applyOrderHistoryPayload(data)
  }, { immediate: true })

  const totalPages = computed(() => Math.ceil(totalOrders.value / itemsPerPage.value))

  const frequentOrdersData = ref<FrequentOrderItem[] | null>(null)
  const isFrequentOrdersLoading = ref(false)
  const orderCount = ref(0)
  const lastOrderDate = ref<string | null>(null)
  const hasResolvedOrderStats = ref(false)

  const hasOrderedRecently = computed(() => {
    if (!lastOrderDate.value) return false
    return Date.now() - Date.parse(lastOrderDate.value) < RECENT_ORDER_WINDOW_DAYS * MS_PER_DAY
  })

  // Do not use useFetch here: plugins/SSR abort the first call before /api/auth,
  // and refresh() then hits that cached miss so the browser never sends a request
  // (hard reload: Last orders tab stays hidden). $fetch runs only when credentials exist.
  const reloadFrequentOrders = async () => {
    const token = unref(accessToken)
    const handle = unref(customerHandle)
    if (!token || !handle) return
    isFrequentOrdersLoading.value = true
    const [err, payload] = await attempt($fetch<LastOrderedProductsResponse>(
      `/api/intern/customers/${handle}/last-ordered-products` as string & {},
      {
        baseURL,
        onRequest({ options }) {
          const headers = new Headers(options.headers)
          headers.set('Authorization', `Bearer ${token}`)
          options.headers = headers
        },
        onResponseError({ response }) {
          if (response.status === 401) void userStore.reloadAuth()
        },
      },
    ))
    isFrequentOrdersLoading.value = false
    // Resolved even on failure: the header waits on this flag, and a permanent
    // skeleton is worse than falling back to the new-customer state.
    hasResolvedOrderStats.value = true
    if (err) return
    frequentOrdersData.value = payload?.products ?? []
    orderCount.value = payload?.orderCount ?? 0
    lastOrderDate.value = payload?.lastOrderDate ?? null
  }

  const ensureListLoaded = () => {
    isListActive.value = true
    if (unref(accessToken) && unref(customerHandle))
      void reloadOrders()
  }

  // Lighter than the full order-history page; unblocks the welcome table
  // without waiting on scale prices / nested order items.
  watch(frequentOrdersData, (items) => {
    if (items == null) return
    hasResolvedOrderHistory.value = true
    if (items.length > 0)
      hasAnyOrders.value = true
  }, { immediate: true })

  watch(accessToken, (token, prevToken) => {
    if (!token || !prevToken || token === prevToken || !unref(customerHandle)) return
    if (isListActive.value) void reloadOrders()
  })

  watch(authWatchSources, ([token, handle], [, prevHandle]) => {
    const cleared = !token || !handle || (prevHandle != null && handle !== prevHandle)
    if (cleared) {
      clearListState()
      isListActive.value = false
      hasAnyOrders.value = false
      hasResolvedOrderHistory.value = false
      orderCount.value = 0
      lastOrderDate.value = null
      hasResolvedOrderStats.value = false
    }
    if (token && handle) {
      applyOrderHistoryPayload(ordersData.value)
      void reloadFrequentOrders()
      // Menu visibility is `totalOrders > 0`, which only comes from this list.
      ensureListLoaded()
    }
  }, { immediate: true })

  // Intentionally does not clear hasAnyOrders / hasResolvedOrderHistory —
  // those are owned by the auth watch (session/customer change) so submitOrder
  // can $reset the list then immediately set hasAnyOrders = true without
  // flipping GTM customer_type back to "new".
  const $reset = async () => {
    clearListState()
    isListActive.value = false
    frequentOrdersData.value = null
    ordersData.value = null
  }

  const nextPage = () => page.value++

  const startPage = () => page.value = 1

  // Awaitable page advance: mutates `page`, then waits for the reactive
  // `useFetch` watcher to start and finish the resulting request. Replaces
  // the polling-based `waitUntilLoaded` in the page component, which could
  // resolve in the gap between `page.value++` and the watcher firing and
  // therefore allow a second `nextPage()` to land before the first fetch.
  const fetchNextPage = async () => {
    page.value++
    await nextTick()
    await until(isLoading).toBe(true, { timeout: 200, throwOnTimeout: false })
    await until(isLoading).toBe(false)
  }

  const toggleFilterValue = (key: string, value: string) => {
    list.value = []
    page.value = 1
    totalOrders.value = 0
    const active = filter.value[key]?.includes(value)
    if (active)
      filter.value[key] = filter.value[key].filter(v => v !== value)
    else
      filter.value[key] = [...(filter.value[key] ?? []), value]
  }

  const clearFilters = (...keys: string[]) => {
    list.value = []
    page.value = 1
    totalOrders.value = 0
    if (keys.length)
      keys.forEach(key => filter.value[key] = [])
    else
      filter.value = {}
  }

  const postOrderRequest = async (
    requestType: OrderRequestType,
    handle: string,
  ) => {
    const token = unref(accessToken)
    if (!token) return
    const urls: Record<OrderRequestType, string> = {
      invoice: `/api/intern/orders/${handle}/request-invoice`,
      orderStatus: `/api/intern/orders/${handle}/request-order-status`,
    }
    const [err] = await attempt($fetch<unknown>(urls[requestType] as string & {}, {
      method: 'POST',
      baseURL,
      onRequest({ options }) {
        const headers = new Headers(options.headers)
        headers.set('Authorization', `Bearer ${token}`)
        options.headers = headers
      },
      onResponseError({ response }) {
        if (response.status === 401) void userStore.reloadAuth()
      },
    }))
    if (err) {
      $storeErrorLog(
        'stores/order-history.ts',
        `Could not post request for ${requestType}`,
        $i18n.t('errors.page.unknown.subtitle'),
        err,
      )
    }
  }

  return {
    $reset,
    ensureListLoaded,
    itemsPerPage,
    page,
    ordersData,
    frequentOrdersData,
    filter,
    list,
    isLoading,
    isFrequentOrdersLoading,
    totalOrders,
    totalPages,
    filterOptions,
    reloadOrders,
    reloadFrequentOrders,
    nextPage,
    fetchNextPage,
    startPage,
    toggleFilterValue,
    clearFilters,
    postOrderRequest,
    hasAnyOrders,
    hasResolvedOrderHistory,
    orderCount,
    hasOrderedRecently,
    hasResolvedOrderStats,
  }
})
